import React from 'react';
import { useSelector } from 'react-redux';
import { gql, useMutation, useQuery } from '@apollo/client';
import { tr } from '~/app/ui/lang';
import Button from '~/app/ui/Button';
import EmptyState from '~/app/ui/EmptyState';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import StatusBadge from '~/app/ui/StatusBadge';
import { Page, PageHeader, Section, Card } from '~/app/ui/Page';
import { Table } from '~/app/ui/Table';
import useCopyText from '~/pages/components/PageEditor/aiWidget/useCopyText';
import useGuideStyles from '~/aiGuide/styles';

const FIELDS = 'id username displayName appName appHost status createdAt lastUsedAt revokedAt revokedReason';

export const CONNECT_DATA = gql`
  query ConnectData { connectorInfo { configured mcpUrl } myAgentConnections { ${FIELDS} } }
`;
export const ALL_CONNECTIONS = gql`query AllAgentConnections { allAgentConnections { ${FIELDS} } }`;
const DISCONNECT = gql`mutation DisconnectAgentConnection($id: ID!) { disconnectAgentConnection(id: $id) }`;
const DISCONNECT_ALL = gql`mutation DisconnectAllAgentConnections { disconnectAllAgentConnections }`;

type Connection = {
  id: string; username: string; displayName: string; appName: string; appHost: string; status: string;
  createdAt: string | null; lastUsedAt: string | null; revokedAt: string | null; revokedReason: string | null;
};

const when = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleDateString();
};

const ConnectionsTable: React.FC<{ rows: Connection[]; showPerson?: boolean; onDisconnect: (c: Connection) => void; label: string; testId: string }> = ({ rows, showPerson, onDisconnect, label, testId }) => (
  <Table label={label} testId={testId}>
    <thead>
      <tr>
        {showPerson && <th scope="col">Person</th>}
        <th scope="col">App</th>
        <th scope="col">Signs in from</th>
        <th scope="col">Connected</th>
        <th scope="col">Last used</th>
        <th scope="col" aria-label="Actions" />
      </tr>
    </thead>
    <tbody>
      {rows.map(c => (
        <tr key={c.id} data-testid={`connection-${c.id}`}>
          {showPerson && <td>{c.displayName} <span style={{ opacity: 0.6 }}>({c.username})</span></td>}
          <td>{c.appName}</td>
          <td>{c.appHost}</td>
          <td>{when(c.createdAt)}</td>
          <td>{c.status === 'active' ? when(c.lastUsedAt) : <StatusBadge tone="gray" label="Disconnected" title={c.revokedReason || undefined} />}</td>
          <td style={{ textAlign: 'right' }}>
            {c.status === 'active' && (
              <Button size="sm" icon="link_off" onClick={() => onDisconnect(c)} data-testid={`disconnect-${c.id}`}>Disconnect</Button>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);

/**
 * Connect your AI: the connector's address and how to add it to Claude,
 * ChatGPT and other AI apps, then the apps connected as you, each with
 * Disconnect. Super admins can also open other people's apps; they stay hidden
 * (and unloaded) until asked for, so nobody else's app is disconnected by accident.
 */
const ConnectScreen: React.FC = () => {
  const c = useGuideStyles();
  const isSuperAdmin = useSelector((state: { app: { user?: { isSuperAdmin?: boolean } } }) => !!state.app.user?.isSuperAdmin);
  const { data, loading, error, refetch } = useQuery<{ connectorInfo: { configured: boolean; mcpUrl: string | null }; myAgentConnections: Connection[] }>(CONNECT_DATA, { fetchPolicy: 'network-only' });
  const [showOthers, setShowOthers] = React.useState(false);
  const all = useQuery<{ allAgentConnections: Connection[] }>(ALL_CONNECTIONS, { fetchPolicy: 'network-only', skip: !isSuperAdmin || !showOthers });
  const [disconnect] = useMutation(DISCONNECT);
  const [disconnectAll] = useMutation(DISCONNECT_ALL);
  const { copy, copied } = useCopyText();
  const [copyFailed, setCopyFailed] = React.useState(false);
  const [confirm, setConfirm] = React.useState<Connection | null>(null);
  const [confirmAll, setConfirmAll] = React.useState(false);

  const url = data?.connectorInfo.mcpUrl || '';
  const mine = (data?.myAgentConnections || []).filter(x => x.status === 'active');
  const everyone = (all.data?.allAgentConnections || []).filter(x => x.status === 'active');
  const others = everyone.filter(x => !mine.some(m => m.id === x.id));
  const isMine = (x: Connection) => mine.some(m => m.id === x.id);
  const doCopy = async (key: string, value: string) => setCopyFailed(!(await copy(key, value)));
  const reload = () => Promise.all([refetch(), isSuperAdmin && showOthers ? all.refetch() : null]);

  if (error && !data) {
    return (
      <Page testId="connect-screen">
        <PageHeader title={tr('Connect your AI', '连接你的 AI')} />
        <EmptyState tone="error" title="Couldn’t load this page" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.message}</EmptyState>
      </Page>
    );
  }

  return (
    <Page testId="connect-screen">
      <PageHeader
        title={tr('Connect your AI', '连接你的 AI')}
        description="Let your own AI app (Claude, ChatGPT and others) work in Genesis as you: find and read pages, create pages, change drafts, add images and send pages for review, all from your chat. It has your access, no more, and it can’t approve, deploy or delete anything."
      />

      <Section title="The address to add" testId="connect-address">
        <Card>
          {loading && !data ? <EmptyState icon="hourglass_empty" title="Loading…" compact /> : null}
          {data && !data.connectorInfo.configured ? (
            <EmptyState icon="power_off" title="Not set up in this environment" compact>Ask a super admin to turn on the connector for this Genesis.</EmptyState>
          ) : null}
          {url ? (
            <div className={c.steps}>
              <p className={c.instruction} data-testid="connector-url">{url}</p>
              <div className={c.row}>
                <Button size="sm" icon={copied === 'url' ? 'check' : 'content_copy'} onClick={() => doCopy('url', url)} data-testid="copy-connector-url">
                  {copied === 'url' ? 'Copied' : 'Copy address'}
                </Button>
              </div>
              {copyFailed && <div className={c.error} role="alert">Your browser didn’t allow copying. Select the address and copy it yourself.</div>}
            </div>
          ) : null}
        </Card>
      </Section>

      {url ? (
        <Section title="Add it to your AI app" hint="When you connect, Genesis asks you to sign in (if you aren’t) and to allow the app. You can disconnect it here any time." testId="connect-steps">
          <Card>
            <div className={c.steps}>
              <div className={c.step}>
                <h3 className={c.stepTitle}>Claude (your own Free, Pro or Max plan)</h3>
                <p className={c.muted}>Customize → Connectors → + Add → Add custom connector. Name it Genesis and paste the address. For sign-in, choose “Sign in when needed” and “Use Claude’s published identity”, then Add. Click Connect and allow it in Genesis. In a chat, turn Genesis on with + (bottom left) → Connectors and ask, for example, “Make a page for our spring event”. Free plans can add one custom connector.</p>
              </div>
              <div className={c.step}>
                <h3 className={c.stepTitle}>Claude for a team (Team or Enterprise)</h3>
                <p className={c.muted}>An owner adds it once for everyone: Organization settings → Connectors → Add → Custom → Web, with this address. Under Authentication choose “Sign in when needed”, and under OAuth client “Use Claude’s published identity”. Then each person opens Customize → Connectors, finds Genesis (marked Custom), clicks Connect and signs in to Genesis as themselves, so Genesis applies their own permissions.</p>
              </div>
              <div className={c.step}>
                <h3 className={c.stepTitle}>Claude Code</h3>
                <p className={c.instruction}>{`claude mcp add --transport http genesis ${url}`}</p>
                <p className={c.muted}>Then run /mcp in Claude Code and choose Genesis to sign in.</p>
                <div className={c.row}>
                  <Button size="sm" icon={copied === 'cc' ? 'check' : 'content_copy'} onClick={() => doCopy('cc', `claude mcp add --transport http genesis ${url}`)}>
                    {copied === 'cc' ? 'Copied' : 'Copy command'}
                  </Button>
                </div>
              </div>
              <div className={c.step}>
                <h3 className={c.stepTitle}>ChatGPT</h3>
                <p className={c.muted}>On chatgpt.com: Plugins → + → Add custom MCP server. Name it Genesis, paste the address, choose OAuth, accept the warning and create it, then sign in to Genesis. In Business, Enterprise and Edu workspaces an admin (or someone they allow) adds it; older ChatGPT versions have this under Settings → Apps with developer mode on. ChatGPT asks you to approve each kind of change (you can approve one for the rest of the chat). If Genesis can only read pages there, your plan or workspace doesn’t allow changes through custom connectors.</p>
              </div>
              <div className={c.step}>
                <h3 className={c.stepTitle}>Other AI apps (Cursor, VS Code and others)</h3>
                <p className={c.muted}>Add a remote MCP server with this address; the app opens Genesis for you to sign in.</p>
              </div>
            </div>
          </Card>
        </Section>
      ) : null}

      <Section title="Your connected apps" testId="connect-mine">
        {mine.length
          ? <ConnectionsTable rows={mine} onDisconnect={setConfirm} label="Your connected AI apps" testId="my-connections" />
          : <EmptyState icon="link" title="No apps connected" compact>{url ? 'Add the address above to your AI app to connect it.' : ''}</EmptyState>}
      </Section>

      {isSuperAdmin && (
        <Section
          title="Other people’s connected apps"
          hint="Only super admins see this. Disconnecting ends an app’s access at once; the person can connect again."
          actions={showOthers ? (
            <div className={c.row}>
              {everyone.length ? <Button size="sm" variant="quiet" icon="link_off" onClick={() => setConfirmAll(true)} data-testid="disconnect-all">Disconnect everyone</Button> : null}
              <Button size="sm" variant="quiet" icon="expand_less" onClick={() => setShowOthers(false)} data-testid="hide-others">Hide</Button>
            </div>
          ) : null}
          testId="connect-all"
        >
          {!showOthers ? (
            <Card>
              <div className={c.row} style={{ justifyContent: 'space-between' }}>
                <p className={c.muted} style={{ margin: 0 }}>Hidden so no one else’s app is disconnected by accident.</p>
                <Button size="sm" icon="expand_more" onClick={() => setShowOthers(true)} data-testid="show-others">Show other people’s apps</Button>
              </div>
            </Card>
          ) : null}
          {showOthers && all.loading && !all.data ? <EmptyState icon="hourglass_empty" title="Loading…" compact /> : null}
          {showOthers && all.error && !all.data ? <EmptyState tone="error" title="Couldn’t load them" compact action={<Button size="sm" icon="refresh" onClick={() => all.refetch()}>Try again</Button>}>{all.error.message}</EmptyState> : null}
          {showOthers && all.data ? (others.length
            ? <ConnectionsTable rows={others} showPerson onDisconnect={setConfirm} label="Other people’s connected AI apps" testId="all-connections" />
            : <EmptyState icon="link" title="No one else has an app connected" compact />) : null}
        </Section>
      )}

      {confirm && (
        <ConfirmDialog
          title={isMine(confirm) ? `Disconnect ${confirm.appName}?` : `Disconnect ${confirm.displayName}’s ${confirm.appName}?`}
          confirmLabel={isMine(confirm) ? 'Disconnect' : `Disconnect ${confirm.displayName}’s app`}
          danger
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            await disconnect({ variables: { id: confirm.id } });
            setConfirm(null);
            await reload();
          }}
        >
          {isMine(confirm)
            ? `${confirm.appName} (${confirm.appHost}) will stop working in Genesis for you right away. You can connect it again from the app.`
            : `This is ${confirm.displayName}’s (${confirm.username}), not yours. ${confirm.appName} (${confirm.appHost}) will stop working in Genesis for them right away; they can connect it again from the app.`}
        </ConfirmDialog>
      )}
      {confirmAll && (
        <ConfirmDialog
          title="Disconnect every AI app?"
          confirmLabel="Disconnect everyone"
          danger
          onClose={() => setConfirmAll(false)}
          onConfirm={async () => {
            await disconnectAll();
            setConfirmAll(false);
            await reload();
          }}
        >
          All {everyone.length} connected apps{mine.length ? ', yours included,' : ''} stop working in Genesis right away. People can connect again from their apps.
        </ConfirmDialog>
      )}
    </Page>
  );
};

export default ConnectScreen;
